import React, { useEffect, useState } from 'react';
import { Download, WifiOff, Share, X } from 'lucide-react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

export function usePWAInstall() {
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as unknown as { standalone?: boolean }).standalone ===
        true;
    setIsInstalled(isStandalone);

    const userAgent = window.navigator.userAgent.toLowerCase();
    const isIOSDevice = /iphone|ipad|ipod/.test(userAgent);
    setIsIOS(isIOSDevice);

    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener(
        'beforeinstallprompt',
        handleBeforeInstallPrompt
      );
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const install = async () => {
    if (!deferredPrompt) return false;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setIsInstalled(true);
      setDeferredPrompt(null);
      return true;
    }
    return false;
  };

  return {
    isInstallable: !!deferredPrompt,
    isInstalled,
    isIOS,
    install,
  };
}

export function useOnlineStatus() {
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return isOnline;
}

export const PWAInstallButton: React.FC<{ className?: string }> = ({
  className,
}) => {
  const { isInstallable, isInstalled, isIOS, install } = usePWAInstall();
  const [showGuide, setShowGuide] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => {
          if (isInstallable) {
            install();
          } else {
            setShowGuide(true);
          }
        }}
        className={
          className ||
          'inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-medium rounded-full border border-[#262626] bg-[#141414] text-[#FAFAFA] hover:bg-[#1C1C1C] transition-colors whitespace-nowrap shrink-0'
        }
        aria-label="Install Quick!Bible App"
      >
        <Download className="w-3.5 h-3.5 text-[#A3A3A3]" />
        <span>Install App</span>
      </button>

      {showGuide && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="pwa-install-title"
        >
          <div className="w-full max-w-md rounded-xl bg-[#141414] border border-[#262626] p-6 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <img
                  src="/quickibible-icon.png"
                  alt="QuickiBible App Icon"
                  className="w-10 h-10 rounded-xl object-contain shadow-md border border-amber-500/30 shrink-0"
                />
                <div>
                  <h3
                    id="pwa-install-title"
                    className="text-base font-bold text-[#FAFAFA]"
                  >
                    Install QuickiBible App
                  </h3>
                  <div className="text-[11px] font-mono text-[#A3A3A3]">
                    Standalone PWA Edition
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowGuide(false)}
                className="p-1.5 rounded-lg text-[#737373] hover:text-[#FAFAFA] cursor-pointer"
                aria-label="Close install guide"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            {isIOS ? (
              <div className="space-y-3 text-sm text-[#D4D4D4]">
                <p>
                  To install Quick!Bible on your iPhone or iPad for full-screen
                  standalone access:
                </p>
                <ol className="list-decimal list-inside space-y-2">
                  <li className="flex items-center gap-2">
                    <span>1. Tap the</span>
                    <Share className="w-4 h-4 text-[#A3A3A3] inline" />
                    <strong>Share</strong> button in Safari.
                  </li>
                  <li>
                    2. Scroll down and select{' '}
                    <strong>Add to Home Screen</strong>.
                  </li>
                </ol>
              </div>
            ) : (
              <div className="space-y-3 text-sm text-[#D4D4D4]">
                <p>
                  Quick!Bible is configured as a Progressive Web App with
                  service-worker caching for desktop and mobile devices.
                </p>
                <p>
                  Click the <strong>Install Quick!Bible</strong> icon in your
                  browser&apos;s address bar or browser menu (
                  <strong>Add to Home Screen / Install App</strong>) to launch
                  Quick!Bible in standalone mode.
                </p>
              </div>
            )}
            <button
              type="button"
              onClick={() => setShowGuide(false)}
              className="mt-5 w-full py-2.5 px-4 rounded-lg bg-[#525252] hover:bg-[#404040] text-white text-sm font-medium transition-colors"
            >
              Got It
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export const OfflineIndicator: React.FC = () => {
  const isOnline = useOnlineStatus();
  if (isOnline) return null;

  return (
    <div
      role="status"
      className="fixed bottom-16 md:bottom-4 left-4 z-50 flex items-center gap-2 rounded-lg bg-amber-600 px-3.5 py-2 text-xs font-medium text-white shadow-lg"
    >
      <WifiOff className="w-4 h-4 shrink-0" />
      <span>Offline Mode — Using cached Scripture & PWA assets</span>
    </div>
  );
};
